import { useNavigate } from 'react-router';
import { BILL_AGE_LABEL, type BillAge } from '@/domain';
import type { OwedByDoctor } from '@/application/ports/FinanceRepository';
import { useFinanceOverview } from '@/application/usecases/useFinance';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { strings } from '@/shared/strings';
import { KpiCard, KpiGrid } from '@/ui/data/KpiCard';
import { useMoney } from '@/ui/money/moneyView';
import { ResponsiveTable, type Column } from '@/ui/data/ResponsiveTable';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { doctorStatementPath } from './paymentPaths';
import styles from './Finance.module.css';

const t = strings.finance;

/** The lists of Payments a figure of the overview opens. */
export type OverviewList = 'unpaid' | 'part' | 'doctors';

/**
 * What the lab is owed, over the lists of Payments. The request of
 * 8 October 2026 (fourth).
 *
 * Four figures, the server's: what is outstanding, how many cases owe it, how
 * many of them are paid in part, and the credit doctors hold. Each is a card
 * to press, and opens the list that holds what it counts: the unpaid cases,
 * the ones paid in part, or the doctors. Under them what is outstanding by how
 * long each case has been open since it was booked, in three ages, each of
 * which opens To be paid narrowed to its cases since the lab's request of
 * 17 October 2026, item 1.
 *
 * A figure that could not be read is an empty card, not one that waits for
 * ever, as on the Doctors and Patients lists.
 */
export function FinanceFigures({
  current,
  age = null,
  onShow,
  onShowAge,
}: {
  /** The list on screen, so the card that counts it reads as pressed. */
  current: string;
  /** The age To be paid is narrowed to, if any, so its figure reads as pressed. */
  age?: BillAge | null;
  onShow: (list: OverviewList) => void;
  /** Opens To be paid narrowed to one age. Without it the ages are figures to read. */
  onShowAge?: (age: BillAge) => void;
}) {
  const overview = useFinanceOverview();
  const money = useMoney();
  const data = overview.data;
  const figure = (value: number | undefined) => (overview.isError ? null : value);

  return (
    <div className={styles.overview}>
      <KpiGrid label={t.overviewLabel}>
        <KpiCard
          label={t.outstanding}
          value={figure(data?.outstanding)}
          format={money}
          pressed={current === 'unpaid' && age === null}
          onPress={() => onShow('unpaid')}
        />
        <KpiCard
          label={t.openCases}
          value={figure(data?.cases)}
          pressed={current === 'unpaid' && age === null}
          onPress={() => onShow('unpaid')}
        />
        <KpiCard
          label={t.partPaid}
          value={figure(data?.partPaid.cases)}
          pressed={current === 'part'}
          onPress={() => onShow('part')}
        />
        <KpiCard
          label={t.creditHeld}
          value={figure(data?.creditHeld)}
          format={money}
          pressed={current === 'doctors'}
          onPress={() => onShow('doctors')}
        />
      </KpiGrid>

      {data !== undefined && (
        <>
          <div className={styles.ages} role="group" aria-label={t.agesLabel}>
            {data.ages.map((each) =>
              onShowAge === undefined ? (
                <div key={each.key} className={styles.age}>
                  <span>{BILL_AGE_LABEL[each.key]}</span>
                  <b>{money(each.outstanding)}</b>
                  <small>{t.casesOwing(each.cases)}</small>
                </div>
              ) : (
                <button
                  key={each.key}
                  type="button"
                  className={`${styles.age} ${styles.agePress}`}
                  aria-pressed={current === 'unpaid' && age === each.key}
                  aria-label={t.openAge(BILL_AGE_LABEL[each.key])}
                  onClick={() => onShowAge(each.key)}
                >
                  <span>{BILL_AGE_LABEL[each.key]}</span>
                  <b>{money(each.outstanding)}</b>
                  <small>{t.casesOwing(each.cases)}</small>
                </button>
              ),
            )}
          </div>
          <p className={styles.hint}>{t.agesHint}</p>
        </>
      )}
    </div>
  );
}

/**
 * What each doctor owes, most first, with the credit each holds: the By
 * doctor list of Payments. A row opens the doctor's statement. The cases with
 * no doctor's record behind them are said under the table, since they belong
 * to no row of it.
 */
export function OwedByDoctorList() {
  const navigate = useNavigate();
  const overview = useFinanceOverview();
  const money = useMoney();
  const layout = useTableLayout('owedByDoctor');

  if (overview.isPending) return <SkeletonRows rows={5} label={strings.common.loadingOrders} />;
  if (overview.isError) return <p className={styles.hint}>{strings.errors.server}</p>;

  const data = overview.data;

  const columns: Column<OwedByDoctor>[] = [
    {
      key: 'code',
      label: t.colId,
      mono: true,
      nowrap: true,
      render: (row) => row.code,
    },
    {
      key: 'name',
      label: t.colDoctor,
      sortable: true,
      sortValue: (row) => row.name ?? row.code,
      render: (row) =>
        row.name == null ? (
          <span className={styles.unset}>{strings.table.hidden}</span>
        ) : (
          <span>
            <span className={styles.name} dir="auto">
              {row.name}
            </span>
            {row.clinic != null && <span className={styles.under}>{row.clinic}</span>}
          </span>
        ),
    },
    {
      key: 'cases',
      label: t.colCases,
      mono: true,
      align: 'right',
      sortable: true,
      sortValue: (row) => row.cases,
      render: (row) => row.cases,
    },
    {
      key: 'outstanding',
      label: t.outstanding,
      mono: true,
      align: 'right',
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.outstanding,
      render: (row) => money(row.outstanding),
    },
    {
      key: 'oldestDays',
      label: t.colOldest,
      mono: true,
      align: 'right',
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.oldestDays,
      render: (row) => (row.cases === 0 ? '' : t.days(row.oldestDays)),
    },
    {
      key: 'credit',
      label: t.colCredit,
      mono: true,
      align: 'right',
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.credit,
      render: (row) => money(row.credit),
    },
  ];

  return (
    <>
      <ResponsiveTable
        customize={layout}
        columns={columns}
        rows={data.doctors}
        rowKey={(row) => row.doctorId}
        onRowClick={(row) => void navigate(doctorStatementPath(row.doctorId))}
        caption={t.doctorsCaption}
        emptyHeading={t.doctorsEmptyHeading}
        emptyDetail={t.doctorsEmptyDetail}
        sortSelectOnMobile
      />
      {data.unlinked.cases > 0 && (
        <p className={`${styles.note} ${styles.gap}`}>
          {t.unlinked}: {t.casesOwing(data.unlinked.cases)}, {money(data.unlinked.outstanding)}
        </p>
      )}
    </>
  );
}
